import React, { createContext, useContext } from 'react';

/**
 * Content Library Context
 * Provides treeData and related values to all child components
 */
const ContentLibraryContext = createContext();

/**
 * Content Library Provider Component
 * @param {Object} props - Component props
 * @param {Object} props.children - Child components
 * @param {Object} props.treeData - Tree data for content library
 * @param {Function} props.setTreeData - Function to set tree data
 * @param {Function} props.handleTreeData - Function to handle tree data
 * @param {Function} props.setIndexes - Function to set indexes
 * @param {Object} props.initialTreeData - Initial tree data
 * @param {Function} props.setInitialTreeData - Function to set initial tree data
 * @param {Function} props.handleRefetch - Function to handle refetch
 * @param {Function} props.fetchTreeView - Function to fetch tree view
 * @param {Function} props.refetchTreeView - Function to refetch tree view
 * @param {boolean} props.loadingTreeView - Loading state for tree view
 * @param {string} props.selectedTabRef - Reference to selected tab
 * @param {Function} props.handleModalTreeSearch - Function to handle modal tree search
 * @param {Function} props.handleTreeSearch - Function to handle tree search
 * @param {Function} props.filterTreeData - Function to filter tree data
 * @param {Function} props.addKey - Function to add key
 * @param {string} props.selectedFolderKey - Selected folder key
 * @param {Function} props.changeSelectedFolderKey - Function to change selected folder key
 * @param {Function} props.onContentLibraryKeySelect - Function to on content library key select
 * @param {string} props.selectedFolderLevel - Selected folder level
 * @param {Function} props.changeSelectedFolderLevel - Function to change selected folder level
 * @param {string} props.selectedFolderName - Selected folder name
 * @param {Function} props.changeSelectedFolderName - Function to change selected folder name
 * @param {Function} props.getDefaultName - Function to get default name
 * @param {Function} props.addChild - Function to add child
 * @param {Function} props.saveDeliverable - Function to save deliverable
 */
export const ContentLibraryProvider = ({
  children,
  treeData,
  setTreeData,
  handleTreeData,
  setIndexes,
  initialTreeData,
  setInitialTreeData,
  handleRefetch,
  fetchTreeView,
  refetchTreeView,
  loadingTreeView,
  selectedTabRef,
  handleModalTreeSearch,
  handleTreeSearch,
  filterTreeData,
  addKey,
  selectedFolderKey,
  changeSelectedFolderKey,
  onContentLibraryKeySelect,
  selectedFolderLevel,
  changeSelectedFolderLevel,
  selectedFolderName,
  changeSelectedFolderName,
  getDefaultName,
  addChild,
  saveDeliverable,
}) => {
  const contextValue = {
    treeData,
    setTreeData,
    handleTreeData,
    setIndexes,
    initialTreeData,
    setInitialTreeData,
    handleRefetch,
    fetchTreeView,
    refetchTreeView,
    loadingTreeView,
    selectedTabRef,
    handleModalTreeSearch,
    handleTreeSearch,
    filterTreeData,
    addKey,
    selectedFolderKey,
    changeSelectedFolderKey,
    onContentLibraryKeySelect,
    selectedFolderLevel,
    changeSelectedFolderLevel,
    selectedFolderName,
    changeSelectedFolderName,
    getDefaultName,
    addChild,
    saveDeliverable,
  };

  return (
    <ContentLibraryContext.Provider value={contextValue}>{children}</ContentLibraryContext.Provider>
  );
};

/**
 * Custom hook to use Content Library Context
 * @returns {Object} Context value containing treeData and related functions
 */
export const useContentLibrary = () => {
  const context = useContext(ContentLibraryContext);
  if (!context) {
    throw new Error('useContentLibrary must be used within a ContentLibraryProvider');
  }
  return context;
};

export default ContentLibraryContext;
